/**
 * Cart PropTypes
 *
 * PropType definitions for shopping cart items and customization data.
 * Includes frame customization options and lens selections.
 *
 * @module propTypes/cart
 */

import PropTypes from "prop-types";
import { lensColorShape } from "../customization/color";

/**
 * Lens customization shape
 * Represents customization options selected for a frame
 *
 * @property {Object} lensType - Selected lens type
 * @property {string} lensType.name - Lens type name
 * @property {Object} color - Selected lens color
 * @property {Object} prescription - Prescription details (optional)
 */
export const customizationShape = PropTypes.shape({
  lensType: PropTypes.shape({
    name: PropTypes.string,
  }),
  color: lensColorShape,
  prescription: PropTypes.object,
});

/**
 * Cart item shape
 * Represents a single item in the shopping cart
 *
 * @property {string|number} id - Unique item identifier
 * @property {string|number} cartItemId - Unique cart item identifier
 * @property {string} name - Product name
 * @property {string} slug - Product URL slug
 * @property {string} type - Product type (frames, sunglasses, accessories)
 * @property {number} price - Item price
 * @property {number} finalPrice - Final calculated price
 * @property {number} quantity - Quantity in cart
 * @property {Array} images - Product images
 * @property {Object} customization - Frame customization (optional)
 * @property {number} discount - Discount percentage (optional)
 */
export const cartItemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  slug: PropTypes.string,
  type: PropTypes.string,
  price: PropTypes.number,
  finalPrice: PropTypes.number,
  quantity: PropTypes.number.isRequired,
  images: PropTypes.arrayOf(PropTypes.string),
  customization: customizationShape,
  discount: PropTypes.number,
});
